import { collection, addDoc, getDocs, orderBy, query, Timestamp, writeBatch, doc, getDoc, setDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { db, app } from '@/config/firebase';
import { cleanUndefinedFields } from '@/utils/firebaseUtils';
import { CriteriaGroup } from '../../../PerformanceCriteria/services/criteriaService';
import { EvaluationScale } from '../../../PerformanceCriteria/services/scalesService';

export interface PerformanceEvaluationCampaign {
    id?: string;
    startDate: Date;
    endDate: Date;
    createdAt?: Date;
    status?: 'active' | 'closed' | 'draft';
    criteriaSnapshot: CriteriaGroup[];
    scalesSnapshot: EvaluationScale[];

    // New fields for questionnaire tracking
    questionnaireId?: string;
    questionnaireTitle?: string;
    introductionCollaborator?: string;
    introductionLeader?: string;
    conclusionCollaborator?: string;
    conclusionLeader?: string;
}

export type EvaluationInviteType = 'SELF' | 'LEADER' | 'CLIENT';

export interface EvaluationInviteTracking {
    recipientName?: string | null;
    recipientEmail?: string | null;
    sentAt?: Date;
    lastSentAt?: Date;
    resendCount?: number;
    openedAt?: Date;
    completedAt?: Date;
    responseId?: string | null;
    tokenId?: string | null;
}

export interface EvaluationInvitesMap {
    self?: EvaluationInviteTracking;
    leader?: EvaluationInviteTracking;
    client?: EvaluationInviteTracking;
}

export interface EvaluationCollaborator {
    id?: string;
    collaboratorId: string;
    collaboratorName: string;
    collaboratorEmail?: string;
    collaboratorPhotoUrl?: string | null;
    responsibleId: string | null;
    responsibleName: string | null;
    responsibleEmail?: string;
    responsiblePhotoUrl?: string | null;
    clientResponsibleName: string | null;
    clientResponsibleEmail: string | null;
    status?: 'pending' | 'completed';
    invites?: EvaluationInvitesMap;
}

function normalizeEmail(value?: string | null): string | null {
    const trimmed = value?.trim().toLowerCase();
    return trimmed || null;
}

function normalizeText(value?: string | null): string | null {
    const trimmed = value?.trim();
    return trimmed || null;
}

function preserveInviteTracking(
    existingCollaborator: EvaluationCollaborator | undefined,
    nextCollaborator: EvaluationCollaborator
): EvaluationInvitesMap {
    const existingInvites = existingCollaborator?.invites;

    if (!existingInvites) {
        return {};
    }

    const shouldKeepLeaderInvite = existingCollaborator?.responsibleId === nextCollaborator.responsibleId
        && normalizeEmail(existingCollaborator?.responsibleEmail) === normalizeEmail(nextCollaborator.responsibleEmail)
        && normalizeText(existingCollaborator?.responsibleName) === normalizeText(nextCollaborator.responsibleName);

    const shouldKeepClientInvite = normalizeEmail(existingCollaborator?.clientResponsibleEmail) === normalizeEmail(nextCollaborator.clientResponsibleEmail)
        && normalizeText(existingCollaborator?.clientResponsibleName) === normalizeText(nextCollaborator.clientResponsibleName);

    const invites: EvaluationInvitesMap = {};

    if (existingInvites.self) {
        invites.self = existingInvites.self;
    }

    if (shouldKeepLeaderInvite && existingInvites.leader) {
        invites.leader = existingInvites.leader;
    }

    if (shouldKeepClientInvite && existingInvites.client) {
        invites.client = existingInvites.client;
    }

    return invites;
}

const CAMPAIGN_COLLECTION = 'performance_evaluations';
const FUNCTIONS_REGION = 'us-central1';

const toDate = (value: any): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') return value.toDate();
    return undefined;
};

const mapInviteTracking = (invite?: any): EvaluationInviteTracking | undefined => {
    if (!invite) return undefined;

    return {
        recipientName: invite.recipientName ?? null,
        recipientEmail: invite.recipientEmail ?? null,
        sentAt: toDate(invite.sentAt),
        lastSentAt: toDate(invite.lastSentAt),
        resendCount: typeof invite.resendCount === 'number' ? invite.resendCount : 0,
        openedAt: toDate(invite.openedAt),
        completedAt: toDate(invite.completedAt),
        responseId: invite.responseId ?? null,
        tokenId: invite.tokenId ?? null,
    };
};

const mapCampaign = (id: string, data: any): PerformanceEvaluationCampaign => ({
    id,
    startDate: toDate(data.startDate) || new Date(),
    endDate: toDate(data.endDate) || new Date(),
    createdAt: toDate(data.createdAt),
    status: data.status,
    criteriaSnapshot: data.criteriaSnapshot || [],
    scalesSnapshot: data.scalesSnapshot || [],
    questionnaireId: data.questionnaireId,
    questionnaireTitle: data.questionnaireTitle,
    introductionCollaborator: data.introductionCollaborator,
    introductionLeader: data.introductionLeader,
    conclusionCollaborator: data.conclusionCollaborator,
    conclusionLeader: data.conclusionLeader
});

const mapEvaluationCollaborator = (id: string, data: any): EvaluationCollaborator => ({
    id,
    collaboratorId: data.collaboratorId,
    collaboratorName: data.collaboratorName,
    collaboratorEmail: data.collaboratorEmail,
    collaboratorPhotoUrl: data.collaboratorPhotoUrl ?? data.photoUrl ?? null,
    responsibleId: data.responsibleId ?? null,
    responsibleName: data.responsibleName ?? null,
    responsibleEmail: data.responsibleEmail,
    responsiblePhotoUrl: data.responsiblePhotoUrl ?? null,
    clientResponsibleName: data.clientResponsibleName ?? null,
    clientResponsibleEmail: data.clientResponsibleEmail ?? null,
    status: data.status,
    invites: {
        self: mapInviteTracking(data.invites?.self),
        leader: mapInviteTracking(data.invites?.leader),
        client: mapInviteTracking(data.invites?.client),
    },
});

const serializeEvaluationCollaboratorForFirestore = (
    collaborator: EvaluationCollaborator,
    options?: {
        createdAt?: Date | Timestamp;
        invites?: EvaluationInvitesMap;
    }
) => {
    const cleanedPayload = cleanUndefinedFields({
        ...collaborator,
        invites: options?.invites ?? collaborator.invites,
    });

    return {
        ...cleanedPayload,
        createdAt: options?.createdAt ?? Timestamp.now(),
    };
};

export const evaluationInviteTrackingUtils = {
    preserveInviteTracking,
    serializeEvaluationCollaboratorForFirestore,
};

export const createPerformanceEvaluationCampaign = async (
    campaign: Omit<PerformanceEvaluationCampaign, 'id' | 'createdAt'>,
    collaborators: EvaluationCollaborator[]
): Promise<string> => {
    try {
        // 1. Create Campaign Document
        const campaignData = {
            startDate: Timestamp.fromDate(campaign.startDate),
            endDate: Timestamp.fromDate(campaign.endDate),
            createdAt: Timestamp.now(),
            status: campaign.status || 'draft',
            criteriaSnapshot: campaign.criteriaSnapshot,
            scalesSnapshot: campaign.scalesSnapshot,
            questionnaireId: campaign.questionnaireId || null,
            questionnaireTitle: campaign.questionnaireTitle || null,
            introductionCollaborator: campaign.introductionCollaborator || null,
            introductionLeader: campaign.introductionLeader || null,
            conclusionCollaborator: campaign.conclusionCollaborator || null,
            conclusionLeader: campaign.conclusionLeader || null
        };

        const docRef = await addDoc(collection(db, CAMPAIGN_COLLECTION), campaignData);
        const campaignId = docRef.id;

        // 2. Create Sub-collection items using Batch
        const batch = writeBatch(db);
        const subCollectionRef = collection(db, CAMPAIGN_COLLECTION, campaignId, 'collaborators');

        collaborators.forEach((collab) => {
            const newDocRef = doc(subCollectionRef); // Auto-ID
            batch.set(newDocRef, serializeEvaluationCollaboratorForFirestore(collab, {
                createdAt: Timestamp.now(),
            }));
        });

        await batch.commit();

        return campaignId;

    } catch (error) {
        console.error("Error creating performance evaluation campaign", error);
        throw error;
    }
};

export const getPerformanceEvaluationCampaigns = async (): Promise<PerformanceEvaluationCampaign[]> => {
    try {
        const q = query(collection(db, CAMPAIGN_COLLECTION), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map((campaignDoc) => mapCampaign(campaignDoc.id, campaignDoc.data()));
    } catch (error) {
        console.error("Error fetching campaigns", error);
        throw error;
    }
};

export const getPerformanceEvaluationCampaignById = async (id: string): Promise<PerformanceEvaluationCampaign | null> => {
    try {
        const docRef = doc(db, CAMPAIGN_COLLECTION, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            return mapCampaign(docSnap.id, data);
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error fetching campaign by id", error);
        throw error;
    }
};
export const RESPONSE_COLLECTION = 'performance_evaluation_responses';

export interface PerformanceEvaluationResponse {
    id?: string;
    campaignId: string;
    collaboratorId: string;
    evaluatorId: string;
    evaluatorEmail: string;
    type: EvaluationInviteType;
    status: 'draft' | 'completed';
    answers: Record<string, number>;
    comments?: Record<string, string>;
    createdAt: any; // Timestamp
    updatedAt: any; // Timestamp
}

/**
 * Saves or updates an evaluation response.
 * Handles both draft and completed statuses.
 */
export const saveEvaluationResponse = async (response: Omit<PerformanceEvaluationResponse, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }) => {
    try {
        const { id, ...data } = response;
        const timestamp = Timestamp.now();

        const payload = {
            ...data,
            updatedAt: timestamp,
            ...(!id ? { createdAt: timestamp } : {})
        };

        if (id) {
            // Update existing
            const docRef = doc(db, RESPONSE_COLLECTION, id);
            await setDoc(docRef, payload, { merge: true }); // Using set with merge for safety
            return id;
        } else {
            // Create new
            const docRef = await addDoc(collection(db, RESPONSE_COLLECTION), payload);
            return docRef.id;
        }
    } catch (error) {
        console.error("Error saving evaluation response", error);
        throw error;
    }
};

/**
 * Checks if the collaborator has any pending self-evaluation.
 * Returns the campaignId if found, null otherwise.
 * 
 * TODO: Logic should be replaced by a real query against open campaigns and existing responses.
 * For now, returns a Mock Campaign ID for testing if needed, or null.
 */
export const checkPendingEvaluation = async (collaboratorId: string): Promise<string | null> => {
    try {
        // 1. Get all active campaigns
        const q = query(
            collection(db, CAMPAIGN_COLLECTION),
            where('status', '==', 'active')
        );
        const campaignsSnap = await getDocs(q);

        if (campaignsSnap.empty) return null;

        // 2. Fetch all COMPLETED responses for this user in parallel
        const responsesQ = query(
            collection(db, RESPONSE_COLLECTION),
            where('evaluatorId', '==', collaboratorId),
            where('status', '==', 'completed')
        );
        const responsesSnap = await getDocs(responsesQ);

        // Map of completed: { campaignId_type_targetId: true }
        const completedMap = new Set(responsesSnap.docs.map(doc => {
            const data = doc.data();
            return `${data.campaignId}_${data.type}_${data.collaboratorId}`;
        }));

        // 3. For each active campaign check assignment
        const checkPromises = campaignsSnap.docs.map(async (campDoc) => {
            const campaignId = campDoc.id;

            const auth = getAuth();
            const userEmail = auth.currentUser?.email;

            if (!userEmail) return null;

            // Due to potential missing index or limitations of or() in some SDK versions,
            // fetch self and leader assignments separately.
            const selfQuery = query(collection(db, CAMPAIGN_COLLECTION, campaignId, 'collaborators'), where('collaboratorEmail', '==', userEmail));
            const leaderQuery = query(collection(db, CAMPAIGN_COLLECTION, campaignId, 'collaborators'), where('responsibleEmail', '==', userEmail));

            const [selfSnap, leaderSnap] = await Promise.all([getDocs(selfQuery), getDocs(leaderQuery)]);

            // Map the distinct results using a map to ensure no duplicates
            const collabMap = new Map<string, EvaluationCollaborator>();
            selfSnap.forEach(d => collabMap.set(d.id, d.data() as EvaluationCollaborator));
            leaderSnap.forEach(d => collabMap.set(d.id, d.data() as EvaluationCollaborator));

            const contributors = Array.from(collabMap.values());

            for (const collabData of contributors) {

                // Check SELF pending
                if (collabData.collaboratorId === collaboratorId) {
                    const selfKey = `${campaignId}_SELF_${collaboratorId}`;
                    if (!completedMap.has(selfKey)) {
                        return campaignId;
                    }
                }

                // Check LEADER pending
                if (collabData.responsibleId === collaboratorId) {
                    const leaderKey = `${campaignId}_LEADER_${collabData.collaboratorId}`;
                    if (!completedMap.has(leaderKey)) {
                        return campaignId;
                    }
                }
            }

            return null;
        });

        const results = await Promise.all(checkPromises);

        // Return the first pending campaign found
        return results.find(id => id !== null) || null;

    } catch (e) {
        console.warn("Failed checking pending evals", e);
        return null;
    }
};

/**
 * Fetches an existing response for a given campaign and user context.
 */
export const getEvaluationResponse = async (campaignId: string, evaluatorId: string, type: 'SELF' | 'LEADER', collaboratorId?: string): Promise<PerformanceEvaluationResponse | null> => {
    try {
        const auth = getAuth();
        const userEmail = auth.currentUser?.email;

        // Although evaluatorId is requested by the code interface,
        // we MUST query using evaluatorEmail as per the new Firestore security rules.
        const queryConstraints: any[] = [
            where('campaignId', '==', campaignId),
            where('evaluatorEmail', '==', userEmail),
            where('type', '==', type)
        ];

        if (collaboratorId) {
            queryConstraints.push(where('collaboratorId', '==', collaboratorId));
        }

        const q = query(
            collection(db, RESPONSE_COLLECTION),
            ...queryConstraints
        );

        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            const docData = snapshot.docs[0].data();
            return { id: snapshot.docs[0].id, ...docData } as PerformanceEvaluationResponse;
        }
        return null;
    } catch (error) {
        console.error("Error fetching response", error);
        return null;
    }
};

/**
 * Fetches all collaborators assigned to a specific campaign.
 * NOTE: This requires ADMIN permissions according to Firestore rules.
 */
export const getPerformanceEvaluationCollaborators = async (campaignId: string): Promise<EvaluationCollaborator[]> => {
    try {
        const q = query(collection(db, CAMPAIGN_COLLECTION, campaignId, 'collaborators'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map((collaboratorDoc) => mapEvaluationCollaborator(collaboratorDoc.id, collaboratorDoc.data()));
    } catch (error) {
        console.error("Error fetching campaign collaborators", error);
        throw error;
    }
};

/**
 * Fetches the user's specific collaborator documents for a campaign (where they are either the collaborator or the leader).
 * This query complies with standard user Firestore security rules.
 */
export const getPerformanceEvaluationCollaboratorByUserEmail = async (campaignId: string, email: string): Promise<EvaluationCollaborator[]> => {
    try {
        const collaboratorsRef = collection(db, CAMPAIGN_COLLECTION, campaignId, 'collaborators');
        const selfQuery = query(collaboratorsRef, where('collaboratorEmail', '==', email));
        const leaderQuery = query(collaboratorsRef, where('responsibleEmail', '==', email));

        const [selfSnap, leaderSnap] = await Promise.all([getDocs(selfQuery), getDocs(leaderQuery)]);
        const collabMap = new Map<string, EvaluationCollaborator>();
        selfSnap.forEach(d => collabMap.set(d.id, mapEvaluationCollaborator(d.id, d.data())));
        leaderSnap.forEach(d => collabMap.set(d.id, mapEvaluationCollaborator(d.id, d.data())));

        return Array.from(collabMap.values());
    } catch (error) {
        console.error("Error fetching campaign collaborator by email", error);
        throw error;
    }
};

/**
 * Updates an existing campaign and its collaborators.
 */
export const updatePerformanceEvaluationCampaign = async (
    id: string,
    data: Omit<PerformanceEvaluationCampaign, 'id' | 'createdAt' | 'criteriaSnapshot' | 'scalesSnapshot'>,
    collaborators: EvaluationCollaborator[]
): Promise<void> => {
    try {
        const campaignRef = doc(db, CAMPAIGN_COLLECTION, id);

        // 1. Update main doc dates
        await setDoc(campaignRef, {
            startDate: Timestamp.fromDate(data.startDate),
            endDate: Timestamp.fromDate(data.endDate),
        }, { merge: true });

        // 2. Update status if changed
        if (data.status) {
            await setDoc(campaignRef, { status: data.status }, { merge: true });
        }

        // 3. Update Collaborators while preserving invite tracking for unchanged rows.
        const batch = writeBatch(db);
        const subCollectionRef = collection(db, CAMPAIGN_COLLECTION, id, 'collaborators');
        const existingDocs = await getDocs(subCollectionRef);
        const existingMap = new Map(existingDocs.docs.map((collaboratorDoc) => [
            collaboratorDoc.data().collaboratorId,
            collaboratorDoc,
        ]));

        const nextCollaboratorIds = new Set(collaborators.map((collaborator) => collaborator.collaboratorId));
        existingDocs.forEach((existingDoc) => {
            if (!nextCollaboratorIds.has(existingDoc.data().collaboratorId)) {
                batch.delete(existingDoc.ref);
            }
        });

        collaborators.forEach((collab) => {
            const existingDoc = existingMap.get(collab.collaboratorId);
            const targetRef = existingDoc?.ref || doc(subCollectionRef);
            const existingData = existingDoc?.data();
            const existingCollaborator = existingDoc
                ? mapEvaluationCollaborator(existingDoc.id, existingData)
                : undefined;

            batch.set(targetRef, {
                ...serializeEvaluationCollaboratorForFirestore(collab, {
                    createdAt: existingData?.createdAt || Timestamp.now(),
                    invites: preserveInviteTracking(existingCollaborator, collab),
                }),
            });
        });

        await batch.commit();

    } catch (error) {
        console.error("Error updating campaign", error);
        throw error;
    }
};

/**
 * Deletes a campaign and its sub-resources.
 */
export const deletePerformanceEvaluationCampaign = async (id: string): Promise<void> => {
    try {
        // 1. Delete subcollection 'collaborators'
        const subCollectionRef = collection(db, CAMPAIGN_COLLECTION, id, 'collaborators');
        const snapshot = await getDocs(subCollectionRef);

        const batch = writeBatch(db);
        snapshot.docs.forEach((doc) => {
            batch.delete(doc.ref);
        });

        // 2. Delete main doc
        const campaignRef = doc(db, CAMPAIGN_COLLECTION, id);
        batch.delete(campaignRef);

        await batch.commit();

    } catch (error) {
        console.error("Error deleting campaign", error);
        throw error;
    }
};

/**
 * Updates only the status of a campaign.
 */
export const updatePerformanceEvaluationStatus = async (id: string, status: 'active' | 'closed' | 'draft'): Promise<void> => {
    try {
        const campaignRef = doc(db, CAMPAIGN_COLLECTION, id);
        await setDoc(campaignRef, { status }, { merge: true });
    } catch (error) {
        console.error("Error updating campaign status", error);
        throw error;
    }
};

export interface MyEvaluationItem {
    campaignId: string;
    campaignTitle: string;
    type: 'SELF' | 'LEADER';
    status: 'pending' | 'draft' | 'completed';
    targetName: string; // "Você" or collaborator name
    targetId: string;
    deadline?: Date;
    responseId?: string;
}

export const getMyEvaluations = async (userId: string, collaboratorId?: string): Promise<MyEvaluationItem[]> => {
    try {
        const results: MyEvaluationItem[] = [];

        // 1. Get Active Campaigns
        const qCampaigns = query(
            collection(db, CAMPAIGN_COLLECTION),
            where('status', '==', 'active')
        );
        const campaignsSnap = await getDocs(qCampaigns);

        if (campaignsSnap.empty) {
            console.log("[DEBUG] getMyEvaluations: No active campaigns found");
            return [];
        }

        const activeCampaigns = campaignsSnap.docs.map(d => {
            const data = d.data();
            return {
                id: d.id,
                ...data,
                startDate: data.startDate?.toDate(),
                endDate: data.endDate?.toDate()
            } as PerformanceEvaluationCampaign;
        });

        // 2. Fetch User Responses (Completed or Draft)
        // Note: The caller passes userId (collaboratorId), but we actually need their email now.
        // Let's refactor this function signature to get email if passed or fetch auth currentUser email directly.
        const auth = getAuth();
        const userEmail = auth.currentUser?.email;

        if (!userEmail) {
            console.log("[DEBUG] getMyEvaluations: No user email available");
            return [];
        }

        const qResponses = query(
            collection(db, RESPONSE_COLLECTION),
            where('evaluatorEmail', '==', userEmail)
        );
        const responsesSnap = await getDocs(qResponses);
        const userResponses = responsesSnap.docs.map(d => ({ id: d.id, ...d.data() } as PerformanceEvaluationResponse));

        // Helper to find response
        const findResponse = (campId: string, targetId: string, type: 'SELF' | 'LEADER') => {
            return userResponses.find(r => r.campaignId === campId && r.collaboratorId === targetId && r.type === type);
        };

        const targetCollabId = collaboratorId || userId;

        // 3. Process Campaigns
        // This part could be heavy if many campaigns, but usually few active.
        for (const campaign of activeCampaigns) {
            if (!campaign.id) continue;

            const collaboratorsRef = collection(db, CAMPAIGN_COLLECTION, campaign.id, 'collaborators');

            // Due to potential missing index or limitations of or() in some SDK versions,
            // fetch self and leader assignments separately.
            const selfQuery = query(collaboratorsRef, where('collaboratorEmail', '==', userEmail));
            const leaderQuery = query(collaboratorsRef, where('responsibleEmail', '==', userEmail));

            const [selfSnap, leaderSnap] = await Promise.all([getDocs(selfQuery), getDocs(leaderQuery)]);

            console.log(`[DEBUG] getMyEvaluations: Campaign ${campaign.id} fetched ${selfSnap.size} self docs and ${leaderSnap.size} leader docs.`);

            // Map the distinct results using a map to ensure no duplicates
            const collabMap = new Map<string, EvaluationCollaborator>();
            selfSnap.forEach(d => collabMap.set(d.id, d.data() as EvaluationCollaborator));
            leaderSnap.forEach(d => collabMap.set(d.id, d.data() as EvaluationCollaborator));

            const collaborators = Array.from(collabMap.values());

            // A. SELF EVALUATION Check
            const mySelfEntry = collaborators.find(c => c.collaboratorId === targetCollabId);
            if (mySelfEntry) {
                const existing = findResponse(campaign.id, targetCollabId, 'SELF');
                results.push({
                    campaignId: campaign.id,
                    campaignTitle: campaign.questionnaireTitle || `Ciclo ${campaign.startDate.toLocaleDateString()} - ${campaign.endDate.toLocaleDateString()}`,
                    type: 'SELF',
                    status: existing ? (existing.status as any) : 'pending',
                    targetName: 'Você',
                    targetId: targetCollabId,
                    deadline: campaign.endDate,
                    responseId: existing?.id
                });
            }

            // B. LEADER EVALUATION Check
            // Find collaborators where I am the responsibleId
            const myTargets = collaborators.filter(c => c.responsibleId === targetCollabId);
            for (const target of myTargets) {
                const existing = findResponse(campaign.id, target.collaboratorId, 'LEADER');
                results.push({
                    campaignId: campaign.id,
                    campaignTitle: campaign.questionnaireTitle || `Ciclo ${campaign.startDate.toLocaleDateString()} - ${campaign.endDate.toLocaleDateString()}`,
                    type: 'LEADER',
                    status: existing ? (existing.status as any) : 'pending',
                    targetName: target.collaboratorName,
                    targetId: target.collaboratorId,
                    deadline: campaign.endDate,
                    responseId: existing?.id
                });
            }
        }

        return results;

    } catch (error) {
        console.error("Error fetching my evaluations", error);
        return [];
    }
};

export const resendCampaignEmails = async (campaignId: string): Promise<{ success: boolean; count: number }> => {
    try {
        const functions = getFunctions(app, FUNCTIONS_REGION);
        const resendEmails = httpsCallable(functions, 'resendCampaignEmails');
        const result = await resendEmails({ campaignId });
        return result.data as { success: boolean; count: number };
    } catch (error) {
        console.error("Error resending emails:", error);
        throw error;
    }
};

export interface ManageEvaluationInvitePayload {
    campaignId: string;
    collaboratorId: string;
    type: EvaluationInviteType;
    mode: 'copy_link' | 'resend_email';
    origin?: string;
}

export interface ManageEvaluationInviteResult {
    success: boolean;
    link: string;
    recipientEmail: string;
    recipientName: string;
}

export const manageEvaluationInvite = async (
    payload: ManageEvaluationInvitePayload
): Promise<ManageEvaluationInviteResult> => {
    try {
        const functions = getFunctions(app, FUNCTIONS_REGION);
        const manageInvite = httpsCallable(functions, 'manageEvaluationInvite');
        const result = await manageInvite(payload);
        return result.data as ManageEvaluationInviteResult;
    } catch (error) {
        console.error("Error managing evaluation invite:", error);
        throw error;
    }
};

export const trackEvaluationInviteOpened = async (params: {
    campaignId: string;
    collaboratorId: string;
    type: Extract<EvaluationInviteType, 'SELF' | 'LEADER'>;
}): Promise<void> => {
    try {
        const functions = getFunctions(app, FUNCTIONS_REGION);
        const trackInvite = httpsCallable(functions, 'trackEvaluationInviteOpened');
        await trackInvite(params);
    } catch (error) {
        console.error("Error tracking evaluation invite opening:", error);
        throw error;
    }
};
